import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useAuthentication } from "hooks/useAuthentication";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import AppInput from "components/AppInput";
import { AppDropdownSelect } from "components/AppDropdownSelect";
import { createStudent, type StudentCreatePayload } from "services/tracking/studentsService";
import { Store } from "store";
import { useContext } from "react";
import { showToast, toastTypes } from "utils";

const validationSchema = Yup.object().shape({
  institution_id: Yup.number().required("Institution requise"),
  first_name: Yup.string().required("Prénom requis"),
  last_name: Yup.string().required("Nom requis"),
  email: Yup.string().email("Email invalide").nullable(),
  parent_email: Yup.string().email("Email parent invalide").nullable(),
});

export default function TrackingStudentCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuthentication();
  const { getInstitutions } = useApi();
  const { data: institutions } = useCache("institutions", () => getInstitutions(1));
  const { dispatch } = useContext(Store);

  const initialValues: StudentCreatePayload = {
    institution_id: user?.user?.institution_id ?? 0,
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    parent_email: "",
    address_label: "",
    latitude: null,
    longitude: null,
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload: StudentCreatePayload = {
          ...values,
          institution_id: Number(values.institution_id),
        };
        const token = user?.auth?.access_token;
        await createStudent(token, payload);

        dispatch(
          showToast({
            toast,
            message: "Élève créé avec succès (API Tracking).",
            status: toastTypes.success,
          })
        );
        navigate("/tracking/students");
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Erreur lors de la création de l'élève (vérifiez que l'API dispose bien de l'endpoint /api/v1/students/).";
        dispatch(
          showToast({
            toast,
            message,
            status: toastTypes.error,
          })
        );
      }
    },
  });

  const { values, handleChange, handleSubmit, isSubmitting, setFieldValue, errors, touched } =
    formik;

  return (
    <TrackingPageLayout title="Ajouter un élève">
      <form onSubmit={handleSubmit}>
        <VStack align="stretch" spacing={4}>
          {!user?.user?.institution_id && (
            <Box>
              <HStack mb={1}>
                <Text fontWeight="medium">Institution</Text>
                <Text as="span" color="red.500">
                  *
                </Text>
              </HStack>
              <AppDropdownSelect
                name="institution_id"
                data={institutions}
                value={values.institution_id != null ? String(values.institution_id) : ""}
                onChange={(name, val) => setFieldValue(name, val === "" || val == null ? 0 : Number(val))}
                placeholder="Sélectionner une institution"
              />
              {touched.institution_id && errors.institution_id && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.institution_id}
                </Text>
              )}
            </Box>
          )}

          <Flex gap={4} flexDirection={{ base: "column", md: "row" }}>
            <Box flex={1}>
              <Text mb={1} fontWeight="medium">
                Prénom *
              </Text>
              <AppInput
                name="first_name"
                value={values.first_name}
                onChange={handleChange}
                placeholder="Prénom de l'élève"
              />
              {touched.first_name && errors.first_name && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.first_name}
                </Text>
              )}
            </Box>
            <Box flex={1}>
              <Text mb={1} fontWeight="medium">
                Nom *
              </Text>
              <AppInput
                name="last_name"
                value={values.last_name}
                onChange={handleChange}
                placeholder="Nom de l'élève"
              />
              {touched.last_name && errors.last_name && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.last_name}
                </Text>
              )}
            </Box>
          </Flex>

          <Flex gap={4} flexDirection={{ base: "column", md: "row" }}>
            <Box flex={1}>
              <Text mb={1} fontWeight="medium">
                Identifiant (username)
              </Text>
              <AppInput
                name="username"
                value={values.username}
                onChange={handleChange}
                placeholder="Identifiant de connexion élève (optionnel)"
              />
            </Box>
            <Box flex={1}>
              <Text mb={1} fontWeight="medium">
                Email élève
              </Text>
              <AppInput
                name="email"
                value={values.email ?? ""}
                onChange={handleChange}
                placeholder="Email de l'élève (optionnel)"
              />
              {touched.email && errors.email && (
                <Text fontSize="sm" color="red.500" mt={1}>
                  {errors.email}
                </Text>
              )}
            </Box>
          </Flex>

          <Box>
            <Text mb={1} fontWeight="medium">
              Email parent
            </Text>
            <AppInput
              name="parent_email"
              value={values.parent_email ?? ""}
              onChange={handleChange}
              placeholder="Email du parent (sera lié à l'élève)"
            />
            {touched.parent_email && errors.parent_email && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.parent_email}
              </Text>
            )}
          </Box>

          <Box>
            <Text mb={1} fontWeight="medium">
              Adresse (libellé)
            </Text>
            <AppInput
              name="address_label"
              value={values.address_label ?? ""}
              onChange={handleChange}
              placeholder="Adresse ou point de prise en charge"
            />
          </Box>

          <Flex gap={4} flexDirection={{ base: "column", md: "row" }}>
            <Box flex={1}>
              <Text mb={1} fontWeight="medium">
                Latitude
              </Text>
              <AppInput
                name="latitude"
                type="number"
                value={values.latitude ?? ""}
                onChange={(e: any) =>
                  setFieldValue("latitude", e.target.value ? Number(e.target.value) : null)
                }
                placeholder="Latitude (optionnel)"
              />
            </Box>
            <Box flex={1}>
              <Text mb={1} fontWeight="medium">
                Longitude
              </Text>
              <AppInput
                name="longitude"
                type="number"
                value={values.longitude ?? ""}
                onChange={(e: any) =>
                  setFieldValue("longitude", e.target.value ? Number(e.target.value) : null)
                }
                placeholder="Longitude (optionnel)"
              />
            </Box>
          </Flex>

          <Flex mt={4} gap={3}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/tracking/students")}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              colorScheme="red_origin"
              isLoading={isSubmitting}
            >
              Enregistrer l&apos;élève
            </Button>
          </Flex>
        </VStack>
      </form>
    </TrackingPageLayout>
  );
}

